import React, {FC, useCallback, useContext, useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {GET_RESOURCE_FIELD_CALENDAR_TEMPLATE} from 'appRedux/actions/resourceFields';
import {GET_WORKFLOW_FIELD_VARIABLES_LIST} from 'appRedux/actions/workflow';
import {RootReducer} from 'appRedux/reducers';

import {AdminContext} from 'contexts/admin/context';
import {AlertContext} from 'contexts/alert/context';

import LanguageSelector from 'components/LanguageSelector/LanguageSelector';

import {getLanguagesWithoutCalendarTemplate} from 'pages/admin/updateResourceField/calendar/templates/helper';
import CreateCalendarTemplateForm from 'components/Forms/ResourceField/calendarTemplate/CreateCalendarTemplateForm';
import UpdateCalendarTemplateForm from 'components/Forms/ResourceField/calendarTemplate/UpdateCalendarTemplateForm';

const ResourceFieldCalendarRemindingTemplate: FC = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const {uuid} = useParams();

    const {myOrganization} = useContext(AdminContext);
    const {showAlert} = useContext(AlertContext);

    const [languageId, setLanguageId] = useState<number | null>(null);

    const getCalendarTemplate = useCallback(
        data => dispatch({type: GET_RESOURCE_FIELD_CALENDAR_TEMPLATE.REQUEST, payload: data}),
        [dispatch],
    );

    const getWorkflowFieldVariablesList = useCallback(
        data => dispatch({type: GET_WORKFLOW_FIELD_VARIABLES_LIST.REQUEST, payload: data}),
        [dispatch],
    );

    const {
        admin: {languageList},
        resourceFields: {currentCalendarRemindingTemplates, currentCalendarItem, isLoadingCalendarTemplate},
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const {workflowUuid} = currentCalendarItem;

    useEffect(() => {
        getCalendarTemplate({
            uuid,
            showAlert,
        });
    }, [uuid]);

    useEffect(() => {
        if (workflowUuid) {
            getWorkflowFieldVariablesList({
                uuid: workflowUuid,
                showAlert,
            });
        }
    }, [workflowUuid]);

    useEffect(() => {
        if (myOrganization) {
            setLanguageId(myOrganization.language);
        }
    }, [myOrganization]);

    if (!myOrganization) return null;

    const currentLanguageTemplate = currentCalendarRemindingTemplates.find(
        template => template.language === Number(languageId),
    );

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography sx={{mb: 2, fontWeight: 700}}>{`${t(
                    'orguser.resourceFields.remindingTemplates',
                )}:`}</Typography>
                <LanguageSelector
                    languageId={Number(languageId)}
                    setLanguageId={setLanguageId}
                    languagesWithBadgeIds={getLanguagesWithoutCalendarTemplate(
                        languageList,
                        currentCalendarRemindingTemplates,
                    )}
                    hideOrganizationLanguage
                />
            </Box>
            {!isLoadingCalendarTemplate && (
                <Box>
                    {currentLanguageTemplate ? (
                        <UpdateCalendarTemplateForm item={currentLanguageTemplate} />
                    ) : (
                        <CreateCalendarTemplateForm uuid={String(uuid)} language={Number(languageId)} />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ResourceFieldCalendarRemindingTemplate;
